/**
 * Tasks panels — widget-first Task Manager (2.1).
 * Uživatelské / Systémové / Modulové úlohy odděleně (owner_kind).
 * Catalog: module-ui-catalog → stejné taby na /core/tasks i node workspace.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Editor from '@monaco-editor/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import i18n from '../../../lib/i18n';
import { Card } from '../../../components/ui/Card';
import { Toggle } from '../../../components/ui/Toggle';
import { AsyncStatusToggle } from '../../../components/ui/AsyncStatusToggle';
import { SearchInput } from '../../../components/ui/SearchInput';
import { Select } from '../../../components/ui/Select';
import { Badge } from '../../../components/ui/Badge';
import { StatCard, StatsGrid } from '../../../components/ui/Stats';
import {
  IDENTITY_ME_QUERY_KEY,
  identityEntityId,
  type IdentityMe,
} from '../../../lib/identity-me';
import type { McpTool } from '../../../types';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';
import { themedConfirm } from '../../../lib/themed-dialogs';
import { visibleProjectId } from '../../../lib/project-id';

let _widgetScope: EffectiveScope = { node: null, identity: null };
export function setTasksWidgetScope(scope: EffectiveScope) {
  _widgetScope = scope;
}
function callMcp<T = unknown>(name: string, args: Record<string, unknown> = {}) {
  return callWidgetTool<T>(name, args, _widgetScope);
}
function scopeKey() {
  return _widgetScope.node ?? 'local';
}
import {
  Activity,
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Copy,
  Filter,
  Globe,
  Hand,
  History,
  Lock,
  Loader2,
  FolderKanban,
  Package,
  Pencil,
  Play,
  Plus,
  Radio,
  RefreshCw,
  Rss,
  Braces,
  ToggleRight,
  Trash2,
  Wrench,
  X,
  XCircle,
} from 'lucide-react';

const PROJECT_ID_RE =
  /(?:PRJ-[0-9A-F]{8}|[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})/i;
const PROJECT_LIFECYCLE_GROUPS = new Set([
  'orchestrator-project-create',
  'orchestrator-project-lifecycle',
]);

interface OrchEnvRef {
  name: string;
  project_id: string | null;
}

/** Vytáhne Project ID z args / jména / popisu / group_name. */
function extractPrjId(task: Task): string | null {
  const args = task.args;
  if (args && typeof args === 'object') {
    const direct = args.project_id ?? args.projectId;
    if (typeof direct === 'string' && PROJECT_ID_RE.test(direct)) return direct;
  }
  const blob = [
    task.name,
    task.description ?? '',
    task.group_name ?? '',
    args ? JSON.stringify(args) : '',
  ].join('\n');
  const m = blob.match(PROJECT_ID_RE);
  return m ? m[0] : null;
}

function sameProjectId(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

/** Vazba úlohy na orchestrátor projekt (ID + env name jako group). */
function resolveTaskProject(
  task: Task,
  envs: OrchEnvRef[],
): { projectId: string | null; envName: string | null; isProject: boolean } {
  const byPrj = extractPrjId(task);
  if (byPrj) {
    const env = envs.find((e) => sameProjectId(e.project_id, byPrj));
    return { projectId: byPrj, envName: env?.name ?? null, isProject: true };
  }

  if (task.group_name) {
    const byName = envs.find((e) => e.name === task.group_name);
    if (byName) {
      return {
        projectId: byName.project_id ?? null,
        envName: byName.name,
        isProject: true,
      };
    }
  }

  if (task.name) {
    const backupEnv = envs.find(
      (e) => task.name === `backup-${e.name}` || task.name.startsWith(`backup-${e.name}-`),
    );
    if (backupEnv) {
      return {
        projectId: backupEnv.project_id ?? null,
        envName: backupEnv.name,
        isProject: true,
      };
    }
  }

  if (
    task.task_class === 'project.lifecycle' ||
    (task.group_name && PROJECT_LIFECYCLE_GROUPS.has(task.group_name))
  ) {
    return { projectId: null, envName: task.group_name, isProject: true };
  }

  return { projectId: null, envName: null, isProject: false };
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Task {
  id: string;
  name: string;
  description: string | null;
  module: string;
  tool: string;
  args: Record<string, unknown> | null;
  trigger_type: 'cron' | 'interval' | 'webhook' | 'event' | 'manual';
  trigger_config: Record<string, unknown>;
  webhook_token: string | null;
  enabled: boolean;
  group_name: string | null;
  last_run_at: string | null;
  last_run_status: string | null;
  next_run_at: string | null;
  /** Vlastnictví a třídy — fáze 0 plánovače v2 (scheduler-v2.md kap. 3). */
  task_class: string;
  owner_kind: 'system' | 'module' | 'user';
  owner_module: string | null;
  owner_entity_id: string | null;
  /** Server-side rozhodnutí (task_list.ts) — jestli přihlášený uživatel smí úlohu editovat. */
  editable: boolean;
}

interface TaskRun {
  id: string;
  task_id: string;
  task_name?: string;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'ok' | 'error';
  triggered_by: string;
  event_name: string | null;
  output: string | null;
  error: string | null;
}

interface AcmEvent {
  id: string;
  event: string;
  source: string;
  data: Record<string, unknown>;
  ts: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TRIGGERS = [
  {
    id: 'cron',
    labelKey: 'tasks.triggerCronLabel',
    Icon: CalendarClock,
    color: 'violet',
    descKey: 'tasks.triggerCronDesc',
  },
  {
    id: 'interval',
    labelKey: 'tasks.triggerIntervalLabel',
    Icon: Clock,
    color: 'blue',
    descKey: 'tasks.triggerIntervalDesc',
  },
  {
    id: 'event',
    labelKey: 'tasks.triggerEventLabel',
    Icon: Radio,
    color: 'emerald',
    descKey: 'tasks.triggerEventDesc',
  },
  {
    id: 'webhook',
    labelKey: 'tasks.triggerWebhookLabel',
    Icon: Globe,
    color: 'amber',
    descKey: 'tasks.triggerWebhookDesc',
  },
  {
    id: 'manual',
    labelKey: 'tasks.triggerManualLabel',
    Icon: Hand,
    color: 'gray',
    descKey: 'tasks.triggerManualDesc',
  },
] as const;

type TriggerType = typeof TRIGGERS[number]['id'];

const TRIGGER_ACTIVE: Record<string, string> = {
  violet: 'text-violet-400 bg-violet-500/10 border-violet-500/40',
  blue: 'text-blue-400   bg-blue-500/10   border-blue-500/40',
  emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/40',
  amber: 'text-amber-400  bg-amber-500/10  border-amber-500/40',
  gray: 'text-gray-300   bg-gray-700/40   border-gray-600',
};

const TRIGGER_BADGE: Record<string, string> = {
  violet: 'text-violet-400 bg-violet-500/10 border-violet-500/20',
  blue: 'text-blue-400   bg-blue-500/10   border-blue-500/20',
  emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  amber: 'text-amber-400  bg-amber-500/10  border-amber-500/20',
  gray: 'text-gray-500   bg-gray-800      border-gray-700',
};

const ACM_EVENTS = [
  'backup.complete',
  'backup.error',
  'apphost.deploy.done',
  'apphost.deploy.error',
  'container.started',
  'container.stopped',
  'module.restarted',
  'deploy.done',
  'deploy.error',
];

const CRON_PRESETS = [
  { labelKey: 'tasks.cronPresetEveryMinute', v: '* * * * *' },
  { labelKey: 'tasks.cronPresetEvery15Min', v: '*/15 * * * *' },
  { labelKey: 'tasks.cronPresetEveryHour', v: '0 * * * *' },
  { labelKey: 'tasks.cronPresetDailyAt3', v: '0 3 * * *' },
  { labelKey: 'tasks.cronPresetEverySunday', v: '0 2 * * 0' },
];

const INTERVAL_PRESETS = [
  { labelKey: 'tasks.intervalPreset1Min', v: '60' },
  { labelKey: 'tasks.intervalPreset5Min', v: '300' },
  { labelKey: 'tasks.intervalPreset15Min', v: '900' },
  { labelKey: 'tasks.intervalPreset1Hour', v: '3600' },
  { labelKey: 'tasks.intervalPreset6Hours', v: '21600' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.abs(Math.floor(diff / 1000));
  const f = diff < 0;
  const v = s < 60
    ? `${s}s`
    : s < 3600
    ? `${Math.floor(s / 60)}min`
    : s < 86400
    ? `${Math.floor(s / 3600)}h`
    : `${Math.floor(s / 86400)}d`;
  return f ? i18n.t('tasks.relTimeFuture', { v }) : i18n.t('tasks.relTimePast', { v });
}

function duration(started: string, finished: string | null): string {
  if (!finished) return '…';
  const ms = new Date(finished).getTime() - new Date(started).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

function triggerLabel(task: Task): string {
  const c = task.trigger_config ?? {};
  if (task.trigger_type === 'cron') return String(c.schedule ?? '—');
  if (task.trigger_type === 'interval') {
    return i18n.t('tasks.everyNSeconds', { seconds: c.seconds ?? '?' });
  }
  if (task.trigger_type === 'webhook') return 'POST /webhook/…';
  if (task.trigger_type === 'event') return String(c.event ?? '?');
  return i18n.t('tasks.manually');
}

function getTriggerMeta(type: string) {
  return TRIGGERS.find((trg) => trg.id === type) ?? TRIGGERS[4]!;
}

// ─── TriggerBadge ─────────────────────────────────────────────────────────────

function TriggerBadge({ type }: { type: string }) {
  const { t } = useTranslation();
  const m = getTriggerMeta(type);
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${
        TRIGGER_BADGE[m.color]
      }`}
    >
      <m.Icon size={9} /> {t(m.labelKey)}
    </span>
  );
}

// ─── ToolPicker ───────────────────────────────────────────────────────────────
//
// Výběr MCP nástroje s:
//  - vyhledáváním (jméno + popis + modul)
//  - rozpadem po modulech
//  - zobrazením inputSchema → automatické prefillování prázdného JSON
//  - popisem vybraného nástroje
//
// Vrací { module, tool, argsTemplate } při výběru.

interface ToolPickerProps {
  tools: McpTool[];
  selectedTool: string; // "module.tool_name" nebo ""
  onSelect: (tool: McpTool) => void;
}

function schemaToTemplate(schema?: Record<string, unknown> | null): string {
  if (!schema || !schema.properties) return '{}';
  const props = schema.properties as Record<
    string,
    { type?: string; default?: unknown; description?: string }
  >;
  const required = (schema.required as string[]) ?? [];
  const obj: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (required.includes(k) || v.default !== undefined) {
      obj[k] = v.default ?? (v.type === 'boolean' ? false : v.type === 'number' ? 0 : '');
    }
  }
  return JSON.stringify(obj, null, 2);
}

function ToolPicker({ tools, selectedTool, onSelect }: ToolPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Zavři při kliknutí mimo
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const q = search.trim().toLowerCase();

  const filtered = useMemo(() =>
    q
      ? tools.filter((tool) =>
        tool.name.includes(q) ||
        (tool.module ?? '').includes(q) ||
        tool.description.toLowerCase().includes(q)
      )
      : tools, [tools, q]);

  // Seskup po modulech
  const grouped = useMemo(() => {
    const map = new Map<string, McpTool[]>();
    for (const tool of filtered) {
      const m = tool.module ?? 'other';
      if (!map.has(m)) map.set(m, []);
      map.get(m)!.push(tool);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const selected = tools.find((tool) => tool.name === selectedTool);

  return (
    <div ref={ref} className='relative'>
      {/* Trigger button */}
      <button
        type='button'
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all text-left ${
          selected
            ? 'bg-gray-900 border-blue-500/40 text-white'
            : 'bg-gray-900 border-gray-800 text-gray-500 hover:border-gray-700'
        }`}
      >
        <Wrench
          size={13}
          className={selected ? 'text-blue-400 flex-shrink-0' : 'text-gray-600 flex-shrink-0'}
        />
        {selected
          ? (
            <span className='flex-1 min-w-0'>
              <span className='font-mono text-xs text-blue-300'>{selected.module}</span>
              <span className='text-gray-500 mx-1'>·</span>
              <span className='font-mono text-xs text-white'>{selected.name}</span>
            </span>
          )
          : <span className='flex-1 text-xs'>{t('tasks.selectToolPlaceholder')}</span>}
        {open
          ? <ChevronUp size={12} className='text-gray-600 flex-shrink-0' />
          : <ChevronDown size={12} className='text-gray-600 flex-shrink-0' />}
      </button>

      {/* Popis vybraného nástroje */}
      {selected && !open && (
        <p className='mt-1.5 text-[11px] text-gray-500 px-1 leading-relaxed'>
          {selected.description}
        </p>
      )}

      {/* Dropdown */}
      {open && (
        <div className='absolute z-50 top-full mt-1.5 left-0 right-0 bg-gray-950 border border-gray-800 rounded-xl shadow-2xl flex flex-col max-h-80'>
          {/* Hledání */}
          <div className='p-2 border-b border-gray-800 flex-shrink-0'>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('tasks.searchToolPlaceholder')}
              className='w-full'
              autoFocus
              resultCount={q ? filtered.length : undefined}
              totalCount={q ? tools.length : undefined}
            />
          </div>

          {/* Seznam */}
          <div className='overflow-y-auto flex-1 py-1'>
            {grouped.length === 0
              ? <p className='text-xs text-gray-600 text-center py-6'>{t('tasks.nothingFound')}</p>
              : (
                grouped.map(([module, mTools]) => (
                  <div key={module}>
                    {/* Hlavička modulu */}
                    <div className='flex items-center gap-1.5 px-3 py-1.5 sticky top-0 bg-gray-950/95'>
                      <Package size={10} className='text-gray-600' />
                      <span className='text-[10px] font-semibold text-gray-500 uppercase tracking-wider'>
                        {module}
                      </span>
                      <span className='text-[10px] text-gray-700 ml-auto'>{mTools.length}</span>
                    </div>

                    {/* Nástroje */}
                    {mTools.map((tool) => {
                      const isActive = tool.name === selectedTool;
                      const reqFields = (tool.inputSchema?.required as string[] | undefined) ?? [];
                      return (
                        <button
                          key={tool.name}
                          type='button'
                          onClick={() => {
                            onSelect(tool);
                            setOpen(false);
                            setSearch('');
                          }}
                          className={`w-full text-left px-3 py-2.5 flex items-start gap-3 hover:bg-gray-800/60 transition-colors ${
                            isActive ? 'bg-blue-500/10' : ''
                          }`}
                        >
                          <Wrench
                            size={11}
                            className={`mt-0.5 flex-shrink-0 ${
                              isActive ? 'text-blue-400' : 'text-gray-600'
                            }`}
                          />
                          <div className='min-w-0 flex-1'>
                            <div className='flex items-center gap-2 flex-wrap'>
                              <span
                                className={`text-xs font-mono font-medium ${
                                  isActive ? 'text-blue-300' : 'text-white'
                                }`}
                              >
                                {tool.name}
                              </span>
                              {reqFields.length > 0 && (
                                <span className='text-[9px] text-gray-600 font-mono'>
                                  {reqFields.slice(0, 3).join(', ')}
                                  {reqFields.length > 3 ? '…' : ''}
                                </span>
                              )}
                            </div>
                            <p className='text-[11px] text-gray-500 mt-0.5 line-clamp-2 leading-relaxed'>
                              {tool.description}
                            </p>
                          </div>
                          {isActive && (
                            <CheckCircle2
                              size={12}
                              className='text-blue-400 flex-shrink-0 mt-0.5'
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── OwnershipBadges ──────────────────────────────────────────────────────────
// Třída + vlastník úlohy (scheduler-v2.md kap. 3.8) — vždy vidět, i pro cizí úlohy.

function OwnershipBadges({ task }: { task: Task }) {
  const { t } = useTranslation();
  const ownerText = task.owner_kind === 'system'
    ? t('tasks.ownerSystem')
    : task.owner_kind === 'module'
    ? t('tasks.ownerModule', { module: task.owner_module ?? '?' })
    : t('tasks.ownerUser');
  return (
    <>
      <Badge variant={task.task_class === 'unclassified' ? 'default' : 'purple'}>
        {task.task_class === 'unclassified' ? t('tasks.classUnclassified') : task.task_class}
      </Badge>
      <Badge variant={task.owner_kind === 'system' ? 'warning' : task.owner_kind === 'module' ? 'info' : 'default'}>
        {ownerText}
      </Badge>
    </>
  );
}

/**
 * `tool` je v přechodu na kanonický `modul.funkce` tvar (viz project_acm_2_1_milestone) —
 * některé tasky ho už mají plně kvalifikovaný (např. "orchestrator.project_action"),
 * jiné ještě starý bare tvar ("backup_group_create"). Bez téhle kontroly `{module}.{tool}`
 * dublovalo modul prefix ("orchestrator.orchestrator.project_action").
 */
function toolDisplayName(module: string, tool: string): string {
  return tool === module || tool.startsWith(`${module}.`) ? tool : `${module}.${tool}`;
}

/** Odkaz/text na vlastnící modul u zamčené úlohy — scheduler-v2.md kap. 3.5/3.8. */
function LockedOwnerHint({ task }: { task: Task }) {
  const { t } = useTranslation();
  if (task.owner_kind === 'module' && task.owner_module) {
    return (
      <Link
        to={`/modules/${task.owner_module}`}
        className='text-blue-400 hover:text-blue-300 underline'
      >
        {t('tasks.lockedEditModuleHint', { module: task.owner_module })}
      </Link>
    );
  }
  if (task.owner_kind === 'system') {
    return <span>{t('tasks.lockedEditSystemHint')}</span>;
  }
  return <span>{t('tasks.lockedEditUserHint')}</span>;
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string | null }) {
  const { t } = useTranslation();
  if (status === 'ok') {
    return (
      <span className='flex items-center gap-1 text-[11px] text-emerald-400'>
        <CheckCircle2 size={11} />{t('tasks.statusOk')}
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className='flex items-center gap-1 text-[11px] text-red-400'>
        <XCircle size={11} />{t('tasks.statusError')}
      </span>
    );
  }
  if (status === 'running') {
    return (
      <span className='flex items-center gap-1 text-[11px] text-blue-400'>
        <Loader2 size={11} className='animate-spin' />{t('tasks.statusRunning')}
      </span>
    );
  }
  return <span className='text-[11px] text-gray-700'>—</span>;
}

// ─── Slide-over Editor ────────────────────────────────────────────────────────

function TaskEditor({ task, onClose }: { task?: Task; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isEdit = !!task;

  const [name, setName] = useState(task?.name ?? '');
  const [desc, setDesc] = useState(task?.description ?? '');
  const [mod, setMod] = useState(task?.module ?? '');
  const [toolName, setToolName] = useState(task?.tool ?? '');
  const [argsStr, setArgsStr] = useState(() =>
    task?.args && typeof task.args === 'object' ? JSON.stringify(task.args, null, 2) : '{}'
  );

  // Načtení seznamu MCP nástrojů (katalog — bare name pro kompatibilitu s task.tool v DB)
  const { data: allTools = [] } = useQuery<McpTool[]>({
    queryKey: ['mcp-tools', scopeKey()],
    queryFn: async () => {
      // Jen module tools + schemas — ne celé consumers/recent (audit 1.7M řádků).
      const cat = await callMcp<{
        module_tools?: Array<{
          name: string;
          aliases?: string[];
          module?: string | null;
          description?: string;
          inputSchema?: Record<string, unknown> | null;
        }>;
      }>('acm-self.tools_catalog', {
        include: ['module_tools'],
        schemas: true,
      });
      return (cat.module_tools ?? []).map((tool) => ({
        file: '',
        // aliases[0] = exportované jméno (task_list); name = namespaced (tasks.task_list)
        name: tool.aliases?.[0] || tool.name,
        description: tool.description ?? '',
        module: tool.module ?? undefined,
        inputSchema: tool.inputSchema ?? null,
      }));
    },
    staleTime: 5 * 60_000,
  });
  const [group, setGroup] = useState(task?.group_name ?? '');
  const [enabled, setEnabled] = useState(task?.enabled ?? true);
  const [trigType, setTrigType] = useState<TriggerType>(
    (task?.trigger_type as TriggerType) ?? 'manual',
  );
  const [argsErr, setArgsErr] = useState('');

  const tc = task?.trigger_config ?? {};
  const [schedule, setSchedule] = useState(String(tc.schedule ?? '0 3 * * *'));
  const [seconds, setSeconds] = useState(String(tc.seconds ?? '300'));
  const [eventPat, setEventPat] = useState(String(tc.event ?? ''));
  const [eventFilter, setEventFilter] = useState(tc.filter ? JSON.stringify(tc.filter) : '');

  const validateArgs = (s: string) => {
    try {
      JSON.parse(s);
      setArgsErr('');
      return true;
    } catch (e) {
      setArgsErr((e as Error).message);
      return false;
    }
  };

  const mut = useMutation({
    mutationFn: async () => {
      if (!validateArgs(argsStr)) throw new Error(t('tasks.fixJsonArgsError'));
      if (!name.trim() || !mod.trim() || !toolName.trim()) {
        throw new Error(t('tasks.requiredFieldsError'));
      }
      // task_create/task_update tooly berou args/event_filter jako JSON string
      // (REST vrstva je dřív stringifikovala v normalizeBody)
      const body: Record<string, unknown> = {
        name: name.trim(),
        description: desc.trim() || null,
        module: mod.trim(),
        tool: toolName.trim(),
        args: argsStr,
        trigger_type: trigType,
        group_name: group.trim() || null,
        enabled,
      };
      if (trigType === 'cron') body.schedule = schedule;
      if (trigType === 'interval') body.seconds = Number(seconds);
      if (trigType === 'event') {
        body.event = eventPat;
        if (eventFilter.trim()) body.event_filter = eventFilter;
      }
      return isEdit
        ? callMcp('tasks.task_update', { id: task!.id, ...body })
        : callMcp('tasks.task_create', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'tasks' });
      toast.success(isEdit ? t('tasks.taskSavedToast') : t('tasks.taskCreatedToast'));
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Portál: slide-over uvnitř widget karty (backdrop-filter) by byl oříznutý.
  return createPortal(
    <div className='fixed inset-0 z-[100] flex'>
      <div className='flex-1 bg-black/50 backdrop-blur-sm min-w-0' onClick={onClose} />
      <div className='w-full max-w-[min(36rem,100vw)] bg-gray-950 border-l border-gray-800 flex flex-col shadow-2xl h-full'>
        {/* Header */}
        <div className='flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-800 flex-shrink-0 gap-3'>
          <h2 className='text-base font-semibold text-white truncate min-w-0'>
            {isEdit ? t('tasks.editTaskTitle', { name: task!.name }) : t('tasks.newTask')}
          </h2>
          <button
            type='button'
            onClick={onClose}
            className='p-1.5 rounded-lg text-gray-600 hover:text-white hover:bg-gray-800 transition-colors shrink-0'
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className='flex-1 overflow-y-auto px-6 py-5 space-y-7'>
          {/* Identifikace */}
          <section className='space-y-3'>
            <p className='text-[11px] font-semibold text-gray-500 uppercase tracking-wider'>
              {t('tasks.sectionIdentification')}
            </p>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className='w-full bg-gray-900 border border-gray-800 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-700 focus:outline-none focus:border-blue-500'
              placeholder={t('tasks.taskNamePlaceholder')}
            />
            <input
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className='w-full bg-gray-900 border border-gray-800 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-700 focus:outline-none focus:border-blue-500'
              placeholder={t('tasks.taskDescPlaceholder')}
            />
          </section>

          {/* Trigger */}
          <section className='space-y-3'>
            <p className='text-[11px] font-semibold text-gray-500 uppercase tracking-wider'>
              {t('tasks.sectionTrigger')}
            </p>
            <div className='grid grid-cols-5 gap-1.5'>
              {TRIGGERS.map((trg) => {
                const active = trigType === trg.id;
                return (
                  <button
                    key={trg.id}
                    onClick={() => setTrigType(trg.id)}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border text-[10px] font-medium transition-all ${
                      active
                        ? TRIGGER_ACTIVE[trg.color]
                        : 'border-gray-800 text-gray-600 hover:text-gray-400 hover:border-gray-700'
                    }`}
                  >
                    <trg.Icon size={16} />
                    {t(trg.labelKey)}
                  </button>
                );
              })}
            </div>

            {/* Trigger config box */}
            <div className='bg-gray-900/50 border border-gray-800 rounded-xl p-4 space-y-3'>
              <p className='text-[10px] text-gray-600'>{t(getTriggerMeta(trigType).descKey)}</p>

              {trigType === 'cron' && (
                <>
                  <input
                    value={schedule}
                    onChange={(e) => setSchedule(e.target.value)}
                    className='w-full bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-violet-500'
                    placeholder='0 3 * * *'
                  />
                  <div className='flex gap-1.5 flex-wrap'>
                    {CRON_PRESETS.map((p) => (
                      <button
                        key={p.v}
                        onClick={() => setSchedule(p.v)}
                        className='px-2 py-1 rounded-lg bg-gray-800/60 text-gray-500 text-[10px] hover:text-white hover:bg-gray-800 transition-colors'
                      >
                        {t(p.labelKey)}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {trigType === 'interval' && (
                <>
                  <div className='flex items-center gap-3'>
                    <input
                      type='number'
                      value={seconds}
                      onChange={(e) => setSeconds(e.target.value)}
                      min={60}
                      className='w-28 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-blue-500'
                    />
                    <span className='text-xs text-gray-500'>
                      {Number(seconds) >= 3600
                        ? `${(Number(seconds) / 3600).toFixed(1)}h`
                        : `${Math.floor(Number(seconds) / 60)}min`}
                    </span>
                  </div>
                  <div className='flex gap-1.5'>
                    {INTERVAL_PRESETS.map((p) => (
                      <button
                        key={p.v}
                        onClick={() => setSeconds(p.v)}
                        className='px-2 py-1 rounded-lg bg-gray-800/60 text-gray-500 text-[10px] hover:text-white hover:bg-gray-800'
                      >
                        {t(p.labelKey)}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {trigType === 'event' && (
                <>
                  <div>
                    <p className='text-[10px] text-gray-600 mb-1'>
                      {t('tasks.eventPatternLabel')}{' '}
                      (glob: <code>backup.*</code>, <code>**.error</code>, <code>**</code>)
                    </p>
                    <input
                      value={eventPat}
                      onChange={(e) => setEventPat(e.target.value)}
                      className='w-full bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-emerald-500'
                      placeholder='backup.complete'
                    />
                    <div className='flex gap-1 mt-1.5 flex-wrap'>
                      {ACM_EVENTS.map((ev) => (
                        <button
                          key={ev}
                          onClick={() => setEventPat(ev)}
                          className='px-1.5 py-0.5 rounded bg-gray-800 text-gray-500 text-[10px] font-mono hover:text-emerald-400 transition-colors'
                        >
                          {ev}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className='text-[10px] text-gray-600 mb-1'>{t('tasks.eventFilterLabel')}</p>
                    <input
                      value={eventFilter}
                      onChange={(e) => setEventFilter(e.target.value)}
                      className='w-full bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-emerald-500'
                      placeholder='{"module":"apphost"}'
                    />
                  </div>
                </>
              )}

              {trigType === 'webhook' && (
                <div className='space-y-1.5 text-xs text-amber-300'>
                  <p>{t('tasks.webhookTokenInfo')}</p>
                  <code className='block font-mono text-[11px] bg-black/30 rounded px-2 py-1.5 text-amber-400'>
                    POST /apiext/tasks/webhook/&#123;token&#125;
                  </code>
                  <p className='text-gray-600'>{t('tasks.webhookTokenAuthNote')}</p>
                </div>
              )}

              {trigType === 'manual' && (
                <p className='text-xs text-gray-600'>
                  {t('tasks.manualRunHint')}{' '}
                  <code className='font-mono text-gray-500'>task_run</code>.
                </p>
              )}
            </div>
          </section>

          {/* Akce */}
          <section className='space-y-3'>
            <p className='text-[11px] font-semibold text-gray-500 uppercase tracking-wider'>
              {t('tasks.sectionAction')}
            </p>

            {/* Tool Picker */}
            <ToolPicker
              tools={allTools}
              selectedTool={toolName}
              onSelect={(tool) => {
                setMod(tool.module ?? '');
                setToolName(tool.name);
                // Pokud jsou args stále výchozí {}, prefilluj z inputSchema
                const current = argsStr.trim().replace(/\s/g, '');
                if (current === '{}' || current === '') {
                  const tmpl = schemaToTemplate(tool.inputSchema);
                  setArgsStr(tmpl);
                  validateArgs(tmpl);
                }
              }}
            />

            {/* Schema hint — povinná pole */}
            {(() => {
              const tool = allTools.find((item) => item.name === toolName);
              const schema = tool?.inputSchema;
              if (!schema?.properties) return null;
              const props = schema.properties as Record<
                string,
                { type?: string; description?: string; enum?: string[] }
              >;
              const required = (schema.required as string[] | undefined) ?? [];
              return (
                <div className='rounded-xl bg-gray-900/60 border border-gray-800 px-3 py-2.5 space-y-1.5'>
                  <p className='text-[10px] text-gray-600 font-medium uppercase tracking-wider flex items-center gap-1.5'>
                    <Braces size={9} className='text-blue-500' /> {t('tasks.toolParamsLabel')}
                  </p>
                  <div className='space-y-1'>
                    {Object.entries(props).map(([key, spec]) => (
                      <div key={key} className='flex items-start gap-2 text-[11px]'>
                        <span
                          className={`font-mono shrink-0 ${
                            required.includes(key) ? 'text-blue-300' : 'text-gray-500'
                          }`}
                        >
                          {key}
                          {required.includes(key) && <span className='text-red-400 ml-0.5'>*</span>}
                        </span>
                        <span className='text-gray-600 leading-relaxed'>
                          {spec.enum
                            ? (
                              <span className='font-mono text-amber-400/70'>
                                {spec.enum.join(' | ')}
                              </span>
                            )
                            : (
                              <>
                                <span className='text-gray-700 font-mono'>{spec.type}</span>
                                {spec.description ? ` — ${spec.description}` : ''}
                              </>
                            )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Args editor */}
            <div>
              <p
                className={`text-[10px] mb-1 flex items-center gap-1.5 ${
                  argsErr ? 'text-red-400' : 'text-gray-600'
                }`}
              >
                {t('tasks.argsJsonLabel')}{argsErr ? ` — ${argsErr}` : ''}
              </p>
              <div
                className={`rounded-xl overflow-hidden border ${
                  argsErr ? 'border-red-800' : 'border-gray-800'
                }`}
              >
                <Editor
                  height='140px'
                  defaultLanguage='json'
                  value={argsStr}
                  onChange={(v) => {
                    const s = v ?? '{}';
                    setArgsStr(s);
                    validateArgs(s);
                  }}
                  theme='vs-dark'
                  options={{
                    minimap: { enabled: false },
                    fontSize: 12,
                    lineNumbers: 'off',
                    scrollBeyondLastLine: false,
                    folding: false,
                    wordWrap: 'on',
                    tabSize: 2,
                    renderLineHighlight: 'none',
                    overviewRulerLanes: 0,
                    hideCursorInOverviewRuler: true,
                    scrollbar: { vertical: 'hidden', horizontal: 'hidden' },
                    padding: { top: 8, bottom: 8 },
                  }}
                />
              </div>
            </div>
          </section>

          {/* Nastavení */}
          <section className='space-y-3'>
            <p className='text-[11px] font-semibold text-gray-500 uppercase tracking-wider'>
              {t('tasks.sectionSettings')}
            </p>
            <div className='grid grid-cols-2 gap-3 items-end'>
              <div>
                <p className='text-[10px] text-gray-600 mb-1'>{t('tasks.groupLabel')}</p>
                <input
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                  className='w-full bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
                  placeholder={t('tasks.groupPlaceholder')}
                />
              </div>
              <Toggle
                value={enabled}
                onSave={(v) => setEnabled(v)}
                label={enabled ? t('tasks.activeLabel') : t('tasks.inactiveLabel')}
              />
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className='flex items-center justify-end gap-2 px-5 sm:px-6 py-4 border-t border-gray-800 flex-shrink-0'>
          <button
            type='button'
            onClick={onClose}
            className='px-4 py-2 text-sm text-gray-500 hover:text-white transition-colors'
          >
            {t('common.cancel')}
          </button>
          <button
            type='button'
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !!argsErr}
            className='flex items-center gap-2 px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-40'
          >
            {mut.isPending && <Loader2 size={13} className='animate-spin' />}
            {isEdit ? t('common.save') : t('tasks.createButton')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Task Card ────────────────────────────────────────────────────────────────

function TaskCard({
  task,
  onEdit,
  projectBadge,
}: {
  task: Task;
  onEdit: () => void;
  /** Volitelný PRJ badge (záložka Projekty). */
  projectBadge?: string | null;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const sk = scopeKey();
  const runMut = useMutation({
    mutationFn: () => callMcp('tasks.task_run', { id: task.id }),
    onSuccess: () => {
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'tasks' });
      toast.success(t('tasks.taskStartedToast', { name: task.name }));
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteMut = useMutation({
    mutationFn: () => callMcp('tasks.task_delete', { id: task.id }),
    onSuccess: () => {
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'tasks' });
      toast.success(t('tasks.taskDeletedToast'));
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const toggleMut = useMutation({
    mutationFn: (v: boolean) => callMcp('tasks.task_update', { id: task.id, enabled: v }),
    onSuccess: () => qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'tasks' }),
    onError: (e: Error) => toast.error(e.message),
  });

  const { data: hist } = useQuery({
    queryKey: ['task-hist', sk, task.id],
    queryFn: () =>
      callMcp<{ runs: TaskRun[] }>('tasks.task_history', { id: task.id, limit: 5 }),
    enabled: expanded,
    staleTime: 15_000,
  });

  return (
    <div
      className={`border rounded-2xl overflow-hidden transition-opacity ${
        task.enabled ? 'border-gray-800' : 'border-gray-800/40 opacity-50'
      }`}
    >
      <div className='flex items-center gap-3 px-4 py-3'>
        {/* AsyncStatusToggle — viz wiki: ui-pattern-async-toggle */}
        <AsyncStatusToggle
          value={task.enabled}
          pending={toggleMut.isPending}
          onToggle={(v) => toggleMut.mutateAsync(v)}
          onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
        />

        {/* Info */}
        <div className='flex-1 min-w-0 cursor-pointer' onClick={() => setExpanded((e) => !e)}>
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='text-sm font-semibold text-white truncate'>{task.name}</span>
            <TriggerBadge type={task.trigger_type} />
            <OwnershipBadges task={task} />
            {projectBadge && (
              <Badge variant='purple' className='text-[10px] font-mono'>
                {projectBadge}
              </Badge>
            )}
            {task.group_name && (
              <span className='text-[10px] text-gray-600 bg-gray-800/60 px-1.5 py-0.5 rounded'>
                {task.group_name}
              </span>
            )}
          </div>
          {!task.editable && (
            <p className='mt-1 text-[10px] text-gray-500 flex items-center gap-1'>
              <Lock size={9} className='flex-shrink-0' /> <LockedOwnerHint task={task} />
            </p>
          )}
          <div className='flex items-center gap-3 mt-0.5 text-[10px] text-gray-600'>
            <span className='font-mono'>{toolDisplayName(task.module, task.tool)}</span>
            <span>{triggerLabel(task)}</span>
            {task.next_run_at && <span className='text-gray-700'>{relTime(task.next_run_at)}</span>}
          </div>
        </div>

        {/* Status */}
        <div className='flex-shrink-0 text-right'>
          <StatusBadge status={task.last_run_status} />
          {task.last_run_at && (
            <div className='text-[9px] text-gray-700 mt-0.5'>{relTime(task.last_run_at)}</div>
          )}
        </div>

        {/* Actions */}
        <div className='flex items-center gap-0.5 flex-shrink-0 ml-1'>
          {task.trigger_type === 'webhook' && task.webhook_token && (
            <button
              onClick={async () => {
                navigator.clipboard.writeText(`/apiext/tasks/webhook/${task.webhook_token}`);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
                toast.success(t('tasks.webhookUrlCopiedToast'));
              }}
              className='p-1.5 rounded-lg text-gray-600 hover:text-amber-400 hover:bg-gray-800 transition-colors'
            >
              {copied
                ? <CheckCircle2 size={13} className='text-emerald-400' />
                : <Copy size={13} />}
            </button>
          )}
          <button
            onClick={() => runMut.mutate()}
            disabled={runMut.isPending || task.last_run_status === 'running'}
            className='p-1.5 rounded-lg text-gray-600 hover:text-emerald-400 hover:bg-gray-800 transition-colors disabled:opacity-30'
            title={t('tasks.runTitle')}
          >
            {runMut.isPending ? <Loader2 size={13} className='animate-spin' /> : <Play size={13} />}
          </button>
          <button
            onClick={onEdit}
            disabled={!task.editable}
            className='p-1.5 rounded-lg text-gray-600 hover:text-blue-400 hover:bg-gray-800 transition-colors disabled:opacity-30 disabled:hover:text-gray-600 disabled:hover:bg-transparent disabled:cursor-not-allowed'
            title={task.editable ? t('tasks.editTitle') : t('tasks.lockedEditTooltip')}
          >
            {task.editable ? <Pencil size={13} /> : <Lock size={13} />}
          </button>
          <button
            onClick={async () => {
              if (await themedConfirm(t('tasks.confirmDeleteTask', { name: task.name }))) deleteMut.mutate();
            }}
            disabled={!task.editable}
            className='p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-gray-800 transition-colors disabled:opacity-30 disabled:hover:text-gray-600 disabled:hover:bg-transparent disabled:cursor-not-allowed'
            title={task.editable ? undefined : t('tasks.lockedDeleteTooltip')}
          >
            <Trash2 size={13} />
          </button>
          <button
            onClick={() => setExpanded((e) => !e)}
            className='p-1.5 text-gray-700 hover:text-gray-400'
          >
            {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className='border-t border-gray-800/60 bg-gray-900/20 px-4 py-3 space-y-3'>
          {task.description && <p className='text-xs text-gray-500'>{task.description}</p>}

          {task.args && Object.keys(task.args).length > 0 && (
            <div>
              <p className='text-[10px] text-gray-600 mb-1'>{t('tasks.argsLabel')}</p>
              <pre className='text-[10px] text-gray-400 font-mono bg-gray-900 rounded-xl p-2.5 overflow-x-auto'>
                {JSON.stringify(task.args, null, 2)}
              </pre>
            </div>
          )}

          {task.trigger_type === 'webhook' && task.webhook_token && (
            <div>
              <p className='text-[10px] text-gray-600 mb-1'>Webhook URL</p>
              <code className='text-[10px] text-amber-400 font-mono bg-gray-900 rounded-xl px-2.5 py-1.5 block'>
                POST /apiext/tasks/webhook/{task.webhook_token}
              </code>
            </div>
          )}

          {/* Mini history */}
          {hist?.runs && hist.runs.length > 0 && (
            <div>
              <p className='text-[10px] text-gray-600 mb-1.5 flex items-center gap-1'>
                <History size={9} /> {t('tasks.lastRunsCount', { count: hist.runs.length })}
              </p>
              <div className='space-y-px'>
                {hist.runs.map((r) => <RunRow key={r.id} run={r} compact />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Run Row ──────────────────────────────────────────────────────────────────

function RunRow({ run, compact = false }: { run: TaskRun; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const hasDetail = !!(run.output || run.error);

  return (
    <div className={compact ? '' : 'border border-gray-800 rounded-2xl overflow-hidden'}>
      <div
        className={`flex items-center gap-3 text-[11px] ${compact ? 'py-0.5' : 'px-4 py-2.5'} ${
          hasDetail ? 'cursor-pointer hover:bg-gray-800/20' : ''
        }`}
        onClick={() => hasDetail && setOpen((o) => !o)}
      >
        <StatusBadge status={run.status} />
        {!compact && (
          <span className='font-medium text-white min-w-0 truncate'>{run.task_name}</span>
        )}
        <span
          className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
            run.triggered_by === 'scheduler'
              ? 'bg-gray-800 text-gray-500'
              : run.triggered_by === 'webhook'
              ? 'bg-amber-900/30 text-amber-400'
              : run.triggered_by === 'event'
              ? 'bg-emerald-900/30 text-emerald-400'
              : run.triggered_by === 'manual'
              ? 'bg-blue-900/30 text-blue-400'
              : 'bg-gray-800 text-gray-500'
          }`}
        >
          {run.triggered_by}
        </span>
        {run.event_name && (
          <span className='text-gray-600 font-mono text-[9px]'>{run.event_name}</span>
        )}
        <span className='flex-1' />
        <span className='text-gray-600 flex-shrink-0'>
          {duration(run.started_at, run.finished_at)}
        </span>
        <span className='text-gray-700 flex-shrink-0'>
          {new Date(run.started_at).toLocaleString('cs')}
        </span>
        {hasDetail && (
          <ChevronDown
            size={10}
            className={`text-gray-700 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        )}
      </div>
      {open && hasDetail && (
        <div className='px-4 pb-3 space-y-2'>
          {run.error && (
            <pre className='text-[10px] text-red-400 font-mono bg-red-950/20 border border-red-900/30 rounded-xl p-2.5 overflow-x-auto whitespace-pre-wrap'>
              {run.error}
            </pre>
          )}
          {run.output && (
            <pre className='text-[10px] text-gray-400 font-mono bg-gray-900 rounded-xl p-2.5 overflow-x-auto whitespace-pre-wrap max-h-48'>
              {run.output}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Tasks list by category ───────────────────────────────────────────────────

type TasksListMode = 'user' | 'system' | 'module' | 'project';

interface TasksListProps {
  mode: TasksListMode;
  allowCreate: boolean;
  hintKey: string;
  emptyKey: string;
}

function TasksListBody({ mode, allowCreate, hintKey, emptyKey }: TasksListProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const sk = scopeKey();
  const [editor, setEditor] = useState<{ open: boolean; task?: Task }>({ open: false });
  const [groupFilter, setGroupFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [showDev, setShowDev] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tasks', sk, mode],
    queryFn: () =>
      callMcp<{ tasks: Task[]; count: number }>('tasks.task_list', {
        include_details: true,
      }),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const { data: meData } = useQuery({
    queryKey: IDENTITY_ME_QUERY_KEY,
    queryFn: () => callMcp<IdentityMe>('identity.me', {}),
    staleTime: 60_000,
    enabled: mode === 'user',
  });
  const myEntityId = identityEntityId(meData);
  const isAdmin = meData?.ok === true && meData.capabilities?.is_admin === true;

  const needEnvs = mode === 'project' || mode === 'module';
  const { data: orchData } = useQuery({
    queryKey: ['orch-list-for-tasks', sk],
    queryFn: async () => {
      try {
        return await callMcp<{ environments?: OrchEnvRef[] }>(
          'orchestrator.orch_list',
          {},
        );
      } catch {
        // Orchestrator nemusí být na nodu — projektový filtr pak jen z task_class / PRJ v args
        return { environments: [] as OrchEnvRef[] };
      }
    },
    staleTime: 60_000,
    enabled: needEnvs,
    retry: false,
  });
  const envs = useMemo(
    () =>
      (orchData?.environments ?? []).map((e) => ({
        name: e.name,
        project_id: e.project_id ?? null,
      })),
    [orchData?.environments],
  );

  const owned = useMemo(() => {
    const all = data?.tasks ?? [];
    return all.filter((task) => {
      const proj = resolveTaskProject(task, envs);

      if (mode === 'user') {
        if (task.owner_kind !== 'user') return false;
        if (!showAllUsers) {
          if (!myEntityId) return false;
          if (task.owner_entity_id !== myEntityId) return false;
        }
        if (
          !showDev &&
          (task.task_class === 'dev.diagnostic' || task.task_class?.startsWith('dev.'))
        ) {
          return false;
        }
        return true;
      }

      if (mode === 'system') {
        return task.owner_kind === 'system';
      }

      if (mode === 'module') {
        // Projektové (lifecycle / env backup) patří do záložky Projekty
        if (task.owner_kind !== 'module') return false;
        if (proj.isProject) return false;
        return true;
      }

      // project
      return proj.isProject;
    });
  }, [data?.tasks, mode, showDev, showAllUsers, myEntityId, envs]);

  const groups = [...new Set(owned.map((task) => task.group_name).filter(Boolean))] as string[];
  const types = [...new Set(owned.map((task) => task.trigger_type))];
  const moduleCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const task of owned) {
      const key = mode === 'module'
        ? (task.owner_module ?? task.module)
        : task.module;
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [owned, mode]);
  const classCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const task of owned) m.set(task.task_class, (m.get(task.task_class) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [owned]);

  /** Project options: z env listu + ID nalezené v úlohách. */
  const projectOptions = useMemo(() => {
    if (mode !== 'project') return [] as Array<{ id: string; label: string; count: number }>;
    const counts = new Map<string, number>();
    for (const task of owned) {
      const { projectId } = resolveTaskProject(task, envs);
      const key = projectId ?? '__unknown__';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const byId = new Map<string, string>();
    for (const e of envs) {
      if (e.project_id) byId.set(e.project_id.toLowerCase(), e.name);
    }
    const rows: Array<{ id: string; label: string; count: number }> = [];
    for (const [id, count] of counts) {
      if (id === '__unknown__') {
        rows.push({
          id,
          label: t('tasks.classUnclassified', 'nezařazeno'),
          count,
        });
        continue;
      }
      const envName = byId.get(id.toLowerCase());
      const shown = visibleProjectId(id, envName);
      rows.push({
        id,
        label: shown && envName && shown !== envName ? `${shown} · ${envName}` : (shown ?? envName ?? id),
        count,
      });
    }
    for (const e of envs) {
      if (!e.project_id) continue;
      const already = [...counts.keys()].some((k) => sameProjectId(k, e.project_id));
      if (!already) {
        const shown = visibleProjectId(e.project_id, e.name);
        rows.push({
          id: e.project_id,
          label: shown && shown !== e.name ? `${shown} · ${e.name}` : (shown ?? e.name),
          count: 0,
        });
      }
    }
    return rows.sort((a, b) => a.label.localeCompare(b.label));
  }, [mode, owned, envs, t]);

  const filtered = owned.filter((task) => {
    if (groupFilter && task.group_name !== groupFilter) return false;
    if (typeFilter && task.trigger_type !== typeFilter) return false;
    if (moduleFilter) {
      const key = mode === 'module'
        ? (task.owner_module ?? task.module)
        : task.module;
      if (key !== moduleFilter) return false;
    }
    if (classFilter && task.task_class !== classFilter) return false;
    if (mode === 'project' && projectFilter) {
      const { projectId } = resolveTaskProject(task, envs);
      if (projectFilter === '__unknown__') {
        if (projectId) return false;
      } else if (!sameProjectId(projectId, projectFilter)) {
        return false;
      }
    }
    return true;
  });

  // Seřazení: třída → jméno (lepší kategorizace v seznamu)
  const filteredSorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const c = (a.task_class || '').localeCompare(b.task_class || '');
      if (c !== 0) return c;
      return a.name.localeCompare(b.name);
    });
  }, [filtered]);

  const totalEnabled = owned.filter((task) => task.enabled).length;
  const totalRunning = owned.filter((task) => task.last_run_status === 'running').length;
  const totalErrors = owned.filter((task) => task.last_run_status === 'error').length;
  const devHidden = mode === 'user' && !showDev
    ? (data?.tasks ?? []).filter((task) => {
      if (task.owner_kind !== 'user') return false;
      if (!showAllUsers && myEntityId && task.owner_entity_id !== myEntityId) return false;
      return task.task_class === 'dev.diagnostic' || task.task_class?.startsWith('dev.');
    }).length
    : 0;

  const hasExtraFilters = !!(typeFilter || groupFilter || moduleFilter || classFilter ||
    projectFilter);
  const invalidate = () =>
    qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'tasks' });

  return (
    <>
      {editor.open && <TaskEditor task={editor.task} onClose={() => setEditor({ open: false })} />}

      <div className='space-y-4'>
        <div className='flex items-start justify-between gap-3 flex-wrap'>
          <p className='text-xs text-gray-500 max-w-xl leading-relaxed'>{t(hintKey)}</p>
          <div className='flex items-center gap-2 flex-shrink-0'>
            <button
              type='button'
              onClick={invalidate}
              className='p-2 rounded-xl text-gray-500 hover:text-white hover:bg-gray-800 transition-colors border border-gray-800'
              title={t('common.refresh', 'Obnovit')}
            >
              <RefreshCw size={14} />
            </button>
            {allowCreate && (
              <button
                type='button'
                onClick={() => setEditor({ open: true })}
                className='flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors'
              >
                <Plus size={14} /> {t('tasks.newTask')}
              </button>
            )}
          </div>
        </div>

        {owned.length > 0 && (
          <StatsGrid columns={4}>
            <StatCard label={t('tasks.statTotal')} value={owned.length} icon={CalendarClock} />
            <StatCard
              label={t('tasks.statActive')}
              value={totalEnabled}
              icon={ToggleRight}
              variant='info'
            />
            <StatCard
              label={t('tasks.statRunning')}
              value={totalRunning}
              icon={Loader2}
              variant={totalRunning > 0 ? 'success' : 'default'}
            />
            <StatCard
              label={t('tasks.statError')}
              value={totalErrors}
              icon={XCircle}
              variant={totalErrors > 0 ? 'error' : 'default'}
            />
          </StatsGrid>
        )}

        {(groups.length > 0 || types.length > 1 || moduleCounts.length > 1 ||
          classCounts.length > 1 || mode === 'user' || mode === 'project') && (
          <div className='flex gap-2 flex-wrap items-center'>
            {mode === 'project' && (
              <Select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                aria-label={t('tasks.projectFilterLabel', 'Projekt')}
              >
                <option value=''>
                  {t('tasks.allProjectsOption', { count: owned.length })}
                </option>
                {projectOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label} ({p.count})
                  </option>
                ))}
              </Select>
            )}
            {moduleCounts.length > 1 && (
              <Select
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
              >
                <option value=''>
                  {t(
                    mode === 'module' ? 'tasks.allOwnerModulesOption' : 'tasks.allModulesOption',
                    { count: owned.length },
                  )}
                </option>
                {moduleCounts.map(([m, n]) => (
                  <option key={m} value={m}>{m} ({n})</option>
                ))}
              </Select>
            )}
            {classCounts.length > 1 && (
              <Select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
              >
                <option value=''>
                  {t('tasks.allClassesOption', { count: owned.length })}
                </option>
                {classCounts.map(([c, n]) => (
                  <option key={c} value={c}>
                    {c === 'unclassified' ? t('tasks.classUnclassified') : c} ({n})
                  </option>
                ))}
              </Select>
            )}
            {(moduleCounts.length > 1 || classCounts.length > 1 || mode === 'project') &&
              (types.length > 0 || groups.length > 0) && (
              <span className='text-gray-800'>|</span>
            )}
            {types.map((ty) => {
              const tm = getTriggerMeta(ty);
              const active = typeFilter === ty;
              return (
                <button
                  key={ty}
                  type='button'
                  onClick={() => setTypeFilter(active ? '' : ty)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[10px] border transition-colors ${
                    active
                      ? TRIGGER_ACTIVE[tm.color]
                      : 'text-gray-600 border-gray-800 hover:text-gray-400'
                  }`}
                >
                  <tm.Icon size={10} /> {t(tm.labelKey)}
                </button>
              );
            })}
            {groups.length > 0 && groups.length <= 12 && (
              <>
                <span className='text-gray-800'>|</span>
                {groups.map((g) => (
                  <button
                    key={g}
                    type='button'
                    onClick={() => setGroupFilter(groupFilter === g ? '' : g)}
                    className={`px-2.5 py-1.5 rounded-xl text-[10px] border transition-colors ${
                      groupFilter === g
                        ? 'bg-gray-700 text-white border-gray-600'
                        : 'text-gray-600 border-gray-800 hover:text-gray-400'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </>
            )}
            {mode === 'user' && (
              <>
                <button
                  type='button'
                  onClick={() => setShowDev((v) => !v)}
                  className={`px-2.5 py-1.5 rounded-xl text-[10px] border transition-colors ${
                    showDev
                      ? 'bg-amber-900/30 text-amber-300 border-amber-700/50'
                      : 'text-gray-600 border-gray-800 hover:text-gray-400'
                  }`}
                >
                  {t('tasks.showDevFilter')}
                  {devHidden > 0 && !showDev ? ` (${devHidden})` : ''}
                </button>
                {isAdmin && (
                  <button
                    type='button'
                    onClick={() => setShowAllUsers((v) => !v)}
                    className={`px-2.5 py-1.5 rounded-xl text-[10px] border transition-colors ${
                      showAllUsers
                        ? 'bg-blue-900/30 text-blue-300 border-blue-700/50'
                        : 'text-gray-600 border-gray-800 hover:text-gray-400'
                    }`}
                  >
                    {t('tasks.showAllUsersFilter', 'Všichni uživatelé')}
                  </button>
                )}
              </>
            )}
            {hasExtraFilters && (
              <button
                type='button'
                onClick={() => {
                  setTypeFilter('');
                  setGroupFilter('');
                  setModuleFilter('');
                  setClassFilter('');
                  setProjectFilter('');
                }}
                className='p-1 text-gray-600 hover:text-white'
              >
                <X size={12} />
              </button>
            )}
          </div>
        )}

        {isLoading
          ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className='h-[58px] bg-gray-800/40 rounded-2xl animate-pulse' />
            ))
          )
          : isError
          ? (
            <div className='text-center py-12 text-red-400'>
              <AlertCircle size={28} className='mx-auto mb-2 text-red-700' />
              <p className='text-sm'>{(error as Error)?.message}</p>
            </div>
          )
          : filteredSorted.length === 0
          ? (
            <div className='text-center py-16 text-gray-500'>
              {mode === 'project'
                ? <FolderKanban size={40} className='mx-auto mb-3 text-gray-800' />
                : <CalendarClock size={40} className='mx-auto mb-3 text-gray-800' />}
              <p>
                {hasExtraFilters ? t('tasks.noResults') : t(emptyKey)}
              </p>
              {allowCreate && !hasExtraFilters && (
                <button
                  type='button'
                  onClick={() => setEditor({ open: true })}
                  className='mt-3 text-sm text-blue-500 hover:text-blue-400'
                >
                  {t('tasks.createFirstTask')}
                </button>
              )}
            </div>
          )
          : (
            <div className='space-y-2'>
              {filteredSorted.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onEdit={() => setEditor({ open: true, task })}
                  projectBadge={
                    mode === 'project'
                      ? visibleProjectId(
                        resolveTaskProject(task, envs).projectId,
                        resolveTaskProject(task, envs).envName,
                      )
                      : null
                  }
                />
              ))}
            </div>
          )}
      </div>
    </>
  );
}

// ─── Widget panels ────────────────────────────────────────────────────────────

export function UserTasksPanel({ scope }: WidgetRenderProps) {
  setTasksWidgetScope(scope ?? { node: null, identity: null });
  return (
    <TasksListBody
      mode='user'
      allowCreate
      hintKey='tasks.userHint'
      emptyKey='tasks.userEmpty'
    />
  );
}

export function SystemTasksPanel({ scope }: WidgetRenderProps) {
  setTasksWidgetScope(scope ?? { node: null, identity: null });
  return (
    <TasksListBody
      mode='system'
      allowCreate={false}
      hintKey='tasks.systemHint'
      emptyKey='tasks.systemEmpty'
    />
  );
}

export function ModuleTasksPanel({ scope }: WidgetRenderProps) {
  setTasksWidgetScope(scope ?? { node: null, identity: null });
  return (
    <TasksListBody
      mode='module'
      allowCreate={false}
      hintKey='tasks.moduleHint'
      emptyKey='tasks.moduleEmpty'
    />
  );
}

export function ProjectTasksPanel({ scope }: WidgetRenderProps) {
  setTasksWidgetScope(scope ?? { node: null, identity: null });
  return (
    <TasksListBody
      mode='project'
      allowCreate={false}
      hintKey='tasks.projectHint'
      emptyKey='tasks.projectEmpty'
    />
  );
}

export function RunsPanel({ scope }: WidgetRenderProps) {
  setTasksWidgetScope(scope ?? { node: null, identity: null });
  const { t } = useTranslation();
  const sk = scopeKey();
  const { data, isLoading } = useQuery({
    queryKey: ['task-runs-global', sk],
    queryFn: () => callMcp<{ runs: TaskRun[] }>('tasks.runs_list', { limit: 100 }),
    staleTime: 10_000,
    refetchInterval: 15_000,
  });

  const runs = data?.runs ?? [];
  if (isLoading) {
    return <div className='text-center py-8 text-gray-600 text-sm'>{t('common.loading')}</div>;
  }
  if (!runs.length) {
    return (
      <div className='text-center py-16 text-gray-500'>
        <History size={36} className='mx-auto mb-3 text-gray-700' />
        <p>{t('tasks.noRunsYet')}</p>
      </div>
    );
  }

  return (
    <div className='space-y-1.5'>
      {runs.map((r) => <RunRow key={r.id} run={r} />)}
    </div>
  );
}

export function EventsPanel({ scope }: WidgetRenderProps) {
  setTasksWidgetScope(scope ?? { node: null, identity: null });
  const { t } = useTranslation();
  const sk = scopeKey();
  const [filter, setFilter] = useState('');
  const [live, setLive] = useState(true);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['acm-events', sk, filter],
    queryFn: () =>
      callMcp<{ events: AcmEvent[] }>('tasks.events_list', {
        limit: 100,
        ...(filter ? { event: filter } : {}),
      }),
    staleTime: 0,
    refetchInterval: live ? 5_000 : false,
  });

  const events = data?.events ?? [];

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-3'>
        <div className='relative flex-1'>
          <Filter size={12} className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-600' />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className='w-full bg-gray-900 border border-gray-800 rounded-xl pl-8 pr-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-blue-500'
            placeholder='backup.*, *.error, apphost.deploy.*…'
          />
        </div>
        <button
          type='button'
          onClick={() => setLive((l) => !l)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs transition-colors ${
            live
              ? 'bg-emerald-900/20 border-emerald-800 text-emerald-400'
              : 'border-gray-800 text-gray-600 hover:text-gray-400'
          }`}
        >
          <Rss size={11} />
          {live ? t('tasks.eventsLive') : t('tasks.eventsPaused')}
        </button>
        <button
          type='button'
          onClick={() => refetch()}
          className='p-2 rounded-xl border border-gray-800 text-gray-600 hover:text-white hover:bg-gray-800 transition-colors'
        >
          <RefreshCw size={12} />
        </button>
      </div>

      {isLoading
        ? <div className='text-center py-8 text-gray-600 text-xs'>{t('common.loading')}</div>
        : events.length === 0
        ? (
          <div className='text-center py-16 text-gray-500'>
            <Activity size={36} className='mx-auto mb-3 text-gray-700' />
            <p>{t('tasks.noEvents')}</p>
            <p className='text-xs mt-1 text-gray-600'>{t('tasks.noEventsHint')}</p>
          </div>
        )
        : (
          <Card>
            <div className='divide-y divide-gray-800/60'>
              {events.map((evt) => (
                <div
                  key={evt.id}
                  className='flex items-center gap-3 px-4 py-2 hover:bg-gray-800/20 text-xs group'
                >
                  <span className='w-1.5 h-1.5 rounded-full bg-emerald-500/70 flex-shrink-0' />
                  <span className='font-mono text-emerald-400 flex-shrink-0 min-w-0'>
                    {evt.event}
                  </span>
                  <span className='text-gray-600 flex-shrink-0 text-[10px]'>{evt.source}</span>
                  {Object.keys(evt.data).length > 0 && (
                    <span className='text-gray-700 font-mono truncate group-hover:text-gray-500 text-[10px]'>
                      {JSON.stringify(evt.data)}
                    </span>
                  )}
                  <span className='flex-1' />
                  <span className='text-gray-700 flex-shrink-0 text-[10px]'>
                    {new Date(evt.ts).toLocaleTimeString('cs')}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}
    </div>
  );
}
